/**
 * Guest wishlist — localStorage-only, versioned key. Mirrors the
 * addIdToRecentlyViewed()/window.recentlyViewedIds pattern in app.js, and
 * reuses the existing dispatchCustomEvent() helper for cross-component sync
 * (product-card hearts, product-page heart, header count, wishlist page all
 * listen for the same 'wishlist:updated' event).
 */
(function () {
  var STORAGE_KEY = 'lgndry_wishlist_v1';

  function readIds() {
    if (!window.localStorage) return [];
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeIds(ids) {
    window.wishlistIds = ids;
    if (window.localStorage) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
      } catch (e) {}
    }
  }

  window.wishlistIds = readIds();

  function isSaved(handle) {
    return window.wishlistIds.indexOf(handle) > -1;
  }

  function addToWishlist(handle) {
    if (!handle || isSaved(handle)) return;
    var ids = window.wishlistIds.slice();
    ids.unshift(handle);
    writeIds(ids);
  }

  function removeFromWishlist(handle) {
    if (!handle) return;
    writeIds(window.wishlistIds.filter(function (id) {
      return id !== handle;
    }));
  }

  // Guarded — this theme doesn't wire up GTM/GA4/Meta Pixel itself today, so
  // these no-op safely until/unless one is installed (via Admin sales channel
  // or app embed), consistent with "add events without interfering".
  function fireAnalytics(eventName, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, detail));
    if (typeof fbq === 'function') {
      fbq('trackCustom', eventName, detail);
    }
  }

  function wishlistStrings() {
    return (window.theme && window.theme.wishlistStrings) || {};
  }

  function toast(message) {
    if (!message) return;
    var el = document.getElementById('WishlistToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'WishlistToast';
      el.className = 'wishlist-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('wishlist-toast--visible');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () {
      el.classList.remove('wishlist-toast--visible');
    }, 2200);
  }

  if (!customElements.get('wishlist-button')) {
    class WishlistButton extends HTMLElement {
      connectedCallback() {
        this.handle = this.dataset.productHandle;
        this.button = this.querySelector('.wishlist-button__toggle');
        if (!this.button || this._bound) return;
        this._bound = true;
        this.button.addEventListener('click', this.onClick.bind(this));
        this._onUpdated = this.onUpdated.bind(this);
        document.addEventListener('wishlist:updated', this._onUpdated);
        this.setState(isSaved(this.handle));
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:updated', this._onUpdated);
      }
      onUpdated(event) {
        if (event.detail && event.detail.handle === this.handle) {
          this.setState(event.detail.saved);
        }
      }
      setState(saved) {
        this.classList.toggle('wishlist-button--saved', saved);
        this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        var strings = wishlistStrings();
        var title = this.dataset.productTitle || '';
        var label = (saved ? strings.remove : strings.add) || '';
        if (label) this.button.setAttribute('aria-label', label.replace('{product}', title));
        var textLabel = this.querySelector('.wishlist-button__label');
        if (textLabel) {
          textLabel.textContent = saved ? (strings.removeLabel || '') : (strings.addLabel || '');
        }
      }
      onClick() {
        var saved = isSaved(this.handle);
        if (saved) {
          removeFromWishlist(this.handle);
        } else {
          addToWishlist(this.handle);
        }
        var nowSaved = !saved;
        dispatchCustomEvent('wishlist:updated', { handle: this.handle, saved: nowSaved });

        var strings = wishlistStrings();
        toast(nowSaved ? strings.addedConfirmation : strings.removedConfirmation);

        fireAnalytics(nowSaved ? 'wishlist_add' : 'wishlist_remove', {
          product_id: this.dataset.productId,
          product_title: this.dataset.productTitle,
          price: this.dataset.productPrice,
          currency: this.dataset.productCurrency
        });
      }
    }
    customElements.define('wishlist-button', WishlistButton);
  }

  if (!customElements.get('wishlist-count')) {
    class WishlistCount extends HTMLElement {
      connectedCallback() {
        this.update();
        this._onUpdated = this.update.bind(this);
        document.addEventListener('wishlist:updated', this._onUpdated);
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:updated', this._onUpdated);
      }
      update() {
        this.textContent = window.wishlistIds.length;
      }
    }
    customElements.define('wishlist-count', WishlistCount);
  }

  if (!customElements.get('wishlist-page')) {
    class WishlistPage extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.emptyState = this.querySelector('[data-wishlist-empty]');
        this.loading = this.querySelector('[data-wishlist-loading]');
        this.render();
        this._onUpdated = this.render.bind(this);
        document.addEventListener('wishlist:updated', this._onUpdated);
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:updated', this._onUpdated);
      }
      render() {
        if (this.grid) this.grid.hidden = true;
        if (this.loading) this.loading.hidden = true;

        if (!window.wishlistIds.length) {
          if (this.emptyState) this.emptyState.hidden = false;
          fireAnalytics('wishlist_view', { item_count: 0 });
          return;
        }
        if (this.emptyState) this.emptyState.hidden = true;
        if (this.loading) this.loading.hidden = false;

        var query = window.wishlistIds.map(function (handle) {
          return 'handle:' + handle;
        }).join(' OR ');
        var url = theme.routes.search_url + '?view=wishlist&type=product&q=' + encodeURIComponent(query);

        fetch(url)
          .then(function (response) { return response.text(); })
          .then(this.onFetched.bind(this))
          .catch(function (e) {
            console.error(e);
            if (this.loading) this.loading.hidden = true;
          }.bind(this));
      }
      onFetched(text) {
        if (this.loading) this.loading.hidden = true;

        var html = document.createElement('div');
        html.innerHTML = text;
        var results = html.querySelector('.wishlist-grid-results');

        if (!results || !results.innerHTML.trim()) {
          if (this.emptyState) this.emptyState.hidden = false;
          return;
        }

        // A saved handle that's deleted/unpublished simply won't come back —
        // prune it from storage so it doesn't linger forever.
        var returnedHandles = Array.prototype.map.call(
          results.querySelectorAll('[data-product-handle]'),
          function (el) { return el.dataset.productHandle; }
        );
        var pruned = window.wishlistIds.filter(function (handle) {
          return returnedHandles.indexOf(handle) > -1;
        });
        if (pruned.length !== window.wishlistIds.length) {
          writeIds(pruned);
        }

        if (this.grid) {
          this.grid.innerHTML = results.innerHTML;
          this.grid.hidden = false;
        }
        fireAnalytics('wishlist_view', { item_count: pruned.length });
      }
    }
    customElements.define('wishlist-page', WishlistPage);
  }

  document.addEventListener('cart:item-added', function (event) {
    if (!document.querySelector('wishlist-page')) return;
    fireAnalytics('wishlist_add_to_cart', (event && event.detail) || {});
  });
})();
